/**
 * SquarePage - 广场页面共享组件
 * 统一 Electron 和 Web 端的 Square 页面
 */

import React, { useState, useEffect, useRef } from 'react';
import { Layout, Spin, message, Modal } from 'antd';
import { type CloudItem } from './CloudSidebarItem';
import { CloudItemCard } from './CloudItemCard';
import { CloudListItem } from './CloudListItem';
import { ContentHeader } from './ContentHeader';
import { CustomScrollbar } from './CustomScrollbar';
import { CustomEmpty } from './CustomEmpty';
import { EncryptedContentPlaceholder } from './EncryptedContentPlaceholder';
import { PostList } from './PostList';
import { PostCard } from './PostCard';
import { PostDetailModal } from './PostDetailModal';
import { CloudShareModal } from './CloudShareModal';
import { useSquarePage } from '../hooks/useSquarePage';
import { toggleCloudPostStar, deleteCloudPost, shareCloudTagGroup } from '../apiClient';
import type { CloudPost } from '../types';
import type { SortBy } from '../utils/postList';
import type { CloudPublicTagGroup, CloudPublicTag } from '../apiClient';

const { Sider, Content } = Layout;

/** 跨平台打开 URL */
const openUrl = (url: string) => {
  if (typeof window !== 'undefined' && (window as any).electronAPI?.openInBrowser) {
    (window as any).electronAPI.openInBrowser(url);
  } else {
    window.open(url, '_blank');
  }
};

/** PostList 渲染参数 */
export interface PostListRenderProps {
  posts: CloudPost[];
  sortBy: SortBy;
  onPostClick: (post: CloudPost) => void;
  emptyText?: string;
  /** 需要高亮的帖子 ID（从通知跳转） */
  highlightPostId?: string | null;
  /** 是否是当前用户分享的（决定操作按钮类型） */
  isOwner?: boolean;
}

/** PostDetailModal 渲染参数 */
export interface PostDetailModalRenderProps {
  post: CloudPost | null;
  onClose: () => void;
  /** 是否是当前用户分享的（决定操作按钮类型） */
  isOwner?: boolean;
}

export interface SquarePageProps {
  /** 当前用户 ID */
  userId?: string;
  /** 是否已登录 */
  isLoggedIn: boolean;
  /** URL 中的 itemId 参数 */
  urlItemId?: string;
  /** 需要高亮的帖子 ID（从通知跳转） */
  highlightPostId?: string | null;
  /** 导航函数 */
  navigate: (path: string) => void;
  /** 页面模式：'square' 显示所有公开项，'subscriptions' 只显示已订阅项 */
  mode?: 'square' | 'subscriptions';
  /** 已订阅项的 ID 集合（仅 mode='subscriptions' 时需要） */
  subscribedItemIds?: Set<string>;
  /** 渲染帖子详情弹窗（可选，不传则使用内置实现） */
  renderPostDetailModal?: (props: PostDetailModalRenderProps) => React.ReactNode;
  /** 渲染帖子列表（可选，不传则使用内置实现） */
  renderPostList?: (props: PostListRenderProps) => React.ReactNode;
}

export const SquarePage: React.FC<SquarePageProps> = ({
  userId,
  isLoggedIn,
  urlItemId,
  highlightPostId,
  navigate,
  mode = 'square',
  subscribedItemIds,
  renderPostDetailModal,
  renderPostList,
}) => {
  // 帖子详情弹窗状态
  const [selectedPost, setSelectedPost] = useState<CloudPost | null>(null);
  // 分享弹窗状态
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [isUpdatingShare, setIsUpdatingShare] = useState(false);

  // 高亮滚动相关
  const postListContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // 无限滚动加载触发器
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // 使用共享 Hook
  const {
    loading,
    loadingContent,
    loadingMore,
    imagesLoading,
    filteredPosts,
    selectedItemId,
    selectedItemType,
    selectedItemDetail,
    selectedCloudTag,
    subscriptionStatusMap,
    sortBy,
    setSortBy,
    expandedTagGroups,
    tagGroupTags,
    // Cloud 分类数据
    myCloudTagGroups,
    othersCloudTagGroups,
    myCloudTags,
    othersCloudTags,
    cloudTopImages,
    cloudTopBloggers,
    // 操作
    handleSelectCloudTagGroup,
    handleSelectCloudTag,
    handleSelectCloudSubTag,
    handleToggleExpand,
    handleSubscribe,
    handleUnsubscribe,
    clearSelection,
    // 密码验证
    needsPassword,
    passwordError,
    verifyingPassword,
    handleVerifyPassword,
    // 已验证的加密 item
    verifiedItems,
    // 更新红点检查
    checkItemHasUpdate,
    // 无限滚动
    hasMore,
    loadMorePosts,
  } = useSquarePage({
    userId,
    isLoggedIn,
    urlItemId,
    navigate,
    mode,
    subscribedItemIds,
  });

  // 计算 isOwner
  const isOwner = selectedItemDetail
    ? (selectedItemDetail.uploaderId || selectedItemDetail.uploader?.id) === userId
    : false;

  // ========== 高亮滚动逻辑 ==========
  useEffect(() => {
    if (!highlightPostId || !postListContainerRef.current || hasScrolledRef.current) return;

    const timer = setTimeout(() => {
      const postElement = postListContainerRef.current?.querySelector(`[data-post-id="${highlightPostId}"]`);
      if (postElement) {
        postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        postElement.classList.add('post-highlight');
        setTimeout(() => {
          postElement.classList.remove('post-highlight');
        }, 3000);
        hasScrolledRef.current = true;
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [highlightPostId, filteredPosts]);

  // ========== 无限滚动加载 ==========
  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    if (!trigger || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMorePosts]);

  // ========== 内置操作回调 ==========
  // 藏为己有（功能开发中）
  const handleCollectToCloud = (post: CloudPost) => {
    message.info('功能正在开发中...');
    console.log('Collect to cloud:', post.id);
  };

  // 预览帖子完整剪藏
  const handleFullClip = (post: CloudPost) => {
    let fullUrl = post.postUrl;
    if (!fullUrl.startsWith('http')) {
      fullUrl = `https://www.xiaohongshu.com${fullUrl.startsWith('/') ? '' : '/'}${fullUrl}`;
    }
    const autoClipUrl = `${fullUrl}#cloudAutoClip`;
    openUrl(autoClipUrl);
  };

  // ========== 默认帖子列表渲染 ==========
  const defaultRenderPostList = ({ posts, sortBy: listSortBy, onPostClick, emptyText, isOwner: listIsOwner }: PostListRenderProps) => (
    <div ref={postListContainerRef}>
      <PostList
        posts={posts}
        sortBy={listSortBy}
        emptyText={emptyText}
        onBloggerClick={(blogger) => {
          if (blogger.profileUrl) {
            openUrl(blogger.profileUrl);
          }
        }}
        renderPostCard={({ post, isPageClip }) => (
          <PostCard
            key={post.id}
            post={post}
            isCloudPost={true}
            showBloggerInfo={isPageClip}
            onClick={() => onPostClick(post)}
            onFullClip={post.isPreview ? () => handleFullClip(post) : undefined}
            onCollectToCloud={!listIsOwner ? handleCollectToCloud : undefined}
          />
        )}
      />
    </div>
  );

  // ========== 默认帖子详情弹窗渲染 ==========
  const defaultRenderPostDetailModal = ({ post, onClose, isOwner: modalIsOwner }: PostDetailModalRenderProps) => {
    if (!post) return null;

    // 自己分享的帖子操作回调
    const ownerCallbacks = modalIsOwner ? {
      onCloudToggleStar: async () => {
        try {
          await toggleCloudPostStar(post.id);
          message.success(post.isStarred ? '已取消星标' : '已加星标');
          window.location.reload();
        } catch (error: any) {
          message.error(error?.message || '操作失败');
        }
      },
      onCloudDelete: () => {
        Modal.confirm({
          title: '确认删除',
          content: `确定要删除帖子 "${post.title}" 吗？`,
          okText: '确认删除',
          okType: 'danger',
          cancelText: '取消',
          onOk: async () => {
            try {
              await deleteCloudPost(post.id);
              message.success('已删除');
              onClose();
              window.location.reload();
            } catch (error: any) {
              message.error(error?.message || '删除失败');
            }
          },
        });
      },
      onCloudEditTags: () => {
        message.info('编辑标签功能请在云剪藏簿中操作');
      },
      cloudIsStarred: post.isStarred,
    } : {};

    return (
      <PostDetailModal
        post={post}
        visible={!!post}
        onClose={onClose}
        platform="web"
        isCloudPost={true}
        {...ownerCallbacks}
      />
    );
  };

  // 使用传入的渲染函数或默认实现
  const actualRenderPostList = renderPostList || defaultRenderPostList;
  const actualRenderPostDetailModal = renderPostDetailModal || defaultRenderPostDetailModal;

  // Cloud 数据
  const allCloudItems = [...myCloudTagGroups, ...othersCloudTagGroups];
  const allCloudTags = [...myCloudTags, ...othersCloudTags];

  // 是否有任何数据
  const hasAnyData = allCloudItems.length > 0 || allCloudTags.length > 0;

  // 渲染 Cloud 侧边栏项
  const renderCloudSidebarItems = (items: CloudPublicTagGroup[] | CloudPublicTag[], itemType: 'tagGroup' | 'tag', startIndex: number = 0) => {
    return items.map((item, index) => (
      <CloudListItem
        key={item.id}
        type={itemType}
        tagGroup={itemType === 'tagGroup' ? item as CloudPublicTagGroup : undefined}
        tag={itemType === 'tag' ? item as CloudPublicTag : undefined}
        topImages={item.topImages?.length ? item.topImages : cloudTopImages[item.id]}
        topBloggers={cloudTopBloggers[item.id]}
        imagesLoading={imagesLoading}
        itemIndex={startIndex + index}
        isSelected={selectedItemId === item.id && selectedItemType === itemType && !selectedCloudTag}
        isExpanded={expandedTagGroups[item.id] || false}
        tags={tagGroupTags[item.id] || (itemType === 'tagGroup' ? (item as CloudPublicTagGroup).tags : [])}
        selectedCloudTag={selectedCloudTag}
        verifiedItems={verifiedItems}
        hasUpdate={checkItemHasUpdate(item.id, item.sharedAt)}
        onClick={() => itemType === 'tagGroup' ? handleSelectCloudTagGroup(item.id) : handleSelectCloudTag(item.id)}
        onToggleExpand={itemType === 'tagGroup' ? () => handleToggleExpand(item.id) : undefined}
        onSelectTag={itemType === 'tagGroup' ? (tagName) => handleSelectCloudSubTag(item.id, tagName) : undefined}
      />
    ));
  };

  return (
    <>
      {/* 二级侧栏 - 广场列表 */}
      <Sider
        width={288}
        className="custom-css-scrollbar"
        style={{
          borderRight: '1px solid var(--custom-border-secondary)',
          padding: '12px',
          overflowY: 'auto',
        }}
      >
        <div style={{ padding: '18px', marginBottom: '0px' }}>
          <h2
            onClick={clearSelection}
            style={{
              margin: 0,
              marginBottom: '8px',
              fontWeight: 600,
              color: 'var(--ant-color-text)',
              cursor: 'pointer',
            }}
          >
            {mode === 'subscriptions' ? '我的订阅' : '广场'}
          </h2>
          <p style={{ fontSize: '12px', lineHeight: '20px', color: 'var(--ant-color-text-secondary)', margin: 0 }}>
            {mode === 'subscriptions' ? '查看你订阅的收藏夹' : '浏览大家公开分享的收藏夹'}
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : !hasAnyData ? (
          <CustomEmpty description="暂无公开分享" />
        ) : (
          <>
            {/* 我分享的 */}
            {(myCloudTagGroups.length > 0 || myCloudTags.length > 0) && (
              <>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--ant-color-primary)',
                  paddingLeft: '18px',
                  paddingBottom: '8px',
                  fontWeight: 500,
                }}>
                  我分享的
                </div>
                {renderCloudSidebarItems(myCloudTagGroups, 'tagGroup', 0)}
                {renderCloudSidebarItems(myCloudTags, 'tag', myCloudTagGroups.length)}

                {(othersCloudTagGroups.length > 0 || othersCloudTags.length > 0) && (
                  <div style={{
                    height: '1px',
                    backgroundColor: 'var(--ant-color-split)',
                    margin: '16px 4px 16px 16px',
                  }} />
                )}
              </>
            )}

            {/* 其他人分享的 */}
            {(othersCloudTagGroups.length > 0 || othersCloudTags.length > 0) && (
              <>
                {renderCloudSidebarItems(othersCloudTagGroups, 'tagGroup', myCloudTagGroups.length + myCloudTags.length)}
                {renderCloudSidebarItems(othersCloudTags, 'tag', myCloudTagGroups.length + myCloudTags.length + othersCloudTagGroups.length)}
              </>
            )}
          </>
        )}
      </Sider>

      {/* 主内容区 */}
      <Content style={{ padding: 0, position: 'relative' }}>
        <CustomScrollbar style={{ height: '100%' }}>
          <div style={{ padding: '16px 24px' }}>
            <Spin spinning={loadingContent}>
              {selectedItemId && selectedItemDetail ? (
                <div>
                  {/* 内容头部 */}
                  <ContentHeader
                    title={selectedCloudTag?.tagName || selectedItemDetail.name}
                    parentName={selectedCloudTag ? selectedItemDetail.name : undefined}
                    // 自己分享的：显示"更新分享信息"按钮
                    showShareButtons={isOwner}
                    isShared={isOwner}
                    isUploading={isUpdatingShare}
                    onUpdateShare={() => setShareModalVisible(true)}
                    // 其他人分享的：显示"订阅"按钮
                    showSubscribeButtons={!isOwner}
                    isSubscribed={subscriptionStatusMap[selectedItemId] || false}
                    onSubscribe={() => handleSubscribe(selectedItemId, selectedItemType!)}
                    onUnsubscribe={() => handleUnsubscribe(selectedItemId, selectedItemType!)}
                    showSortDropdown
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                  />

                  {/* 帖子列表或加密占位 */}
                  <div style={{ marginTop: '16px' }}>
                    {needsPassword ? (
                      <EncryptedContentPlaceholder
                        onPasswordSubmit={handleVerifyPassword}
                        loading={verifyingPassword}
                        error={passwordError}
                      />
                    ) : (
                      <>
                        {actualRenderPostList({
                          posts: filteredPosts,
                          sortBy,
                          onPostClick: (post: CloudPost) => {
                            setSelectedPost(post);
                          },
                          emptyText: '暂无帖子数据',
                          highlightPostId,
                          isOwner,
                        })}

                        {/* 无限滚动加载触发器 */}
                        {hasMore && (
                          <div
                            ref={loadMoreTriggerRef}
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              padding: '20px 0',
                              color: 'var(--text-secondary)',
                            }}
                          >
                            {loadingMore ? (
                              <Spin size="small" />
                            ) : (
                              <span style={{ fontSize: '13px' }}>滚动加载更多...</span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <SquareEmptyState
                  recommendedItems={[
                    // Cloud 数据（过滤掉加密的）
                    ...[...myCloudTagGroups, ...othersCloudTagGroups]
                      .filter(tg => !tg.isEncrypted)
                      .sort((a, b) => (b.postCount || 0) - (a.postCount || 0)),
                    ...[...myCloudTags, ...othersCloudTags]
                      .filter(t => !t.isEncrypted)
                      .sort((a, b) => (b.postCount || 0) - (a.postCount || 0)),
                  ].slice(0, 4)}
                  cloudTopImages={cloudTopImages}
                  cloudTopBloggers={cloudTopBloggers}
                  onItemClick={(item, type) => {
                    if (type === 'tagGroup') {
                      handleSelectCloudTagGroup(item.id);
                    } else {
                      handleSelectCloudTag(item.id);
                    }
                  }}
                  cloudTagGroupIds={new Set([...myCloudTagGroups, ...othersCloudTagGroups].map(tg => tg.id))}
                  cloudTagIds={new Set([...myCloudTags, ...othersCloudTags].map(t => t.id))}
                />
              )}
            </Spin>
          </div>
        </CustomScrollbar>
      </Content>

      {/* 帖子详情弹窗 */}
      {actualRenderPostDetailModal({
        post: selectedPost,
        onClose: () => setSelectedPost(null),
        isOwner,
      })}

      {/* 更新分享信息弹窗 */}
      {selectedItemDetail && isOwner && (
        <CloudShareModal
          visible={shareModalVisible}
          onCancel={() => setShareModalVisible(false)}
          type={selectedItemType === 'tagGroup' ? 'cloudTagGroup' : 'cloudTag'}
          itemId={selectedItemId!}
          itemName={(selectedItemDetail as any).originalName || selectedItemDetail.name}
          isPublic={true}
          currentAlias={(selectedItemDetail as any).alias || ''}
          currentDescription={(selectedItemDetail as any).shareDescription || (selectedItemDetail as any).description || ''}
          currentIsAnonymous={(selectedItemDetail as any).isAnonymous || false}
          currentIsEncrypted={(selectedItemDetail as any).isEncrypted || false}
          onShare={async (params) => {
            setIsUpdatingShare(true);
            try {
              await shareCloudTagGroup(selectedItemId!, {
                isPublic: params.isPublic,
                alias: params.alias,
                description: params.description,
                isAnonymous: params.isAnonymous,
                isEncrypted: params.isEncrypted,
                accessPassword: params.accessPassword,
              });
              setShareModalVisible(false);
              message.success('分享信息已更新');
              window.location.reload();
            } finally {
              setIsUpdatingShare(false);
            }
          }}
        />
      )}
    </>
  );
};

/**
 * 广场初始态组件 - 未选中任何项目时显示
 */
interface SquareEmptyStateProps {
  recommendedItems: CloudItem[];
  cloudTopImages?: Record<string, string[]>;
  cloudTopBloggers?: Record<string, Array<{ nickname: string; avatarUrl: string | null }>>;
  cloudTagGroupIds?: Set<string>;
  cloudTagIds?: Set<string>;
  onItemClick: (item: CloudItem, type: 'tagGroup' | 'tag') => void;
}

const SquareEmptyState: React.FC<SquareEmptyStateProps> = ({
  recommendedItems,
  cloudTopImages = {},
  cloudTopBloggers = {},
  onItemClick,
}) => {
  if (recommendedItems.length === 0) {
    return (
      <CustomEmpty
        description="暂无推荐内容"
        style={{ marginTop: '100px' }}
      />
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      padding: '64px 20px 64px 20px',
    }}>
      {/* 大标题 */}
      <h1 style={{
        fontSize: 28,
        fontWeight: 700,
        color: 'var(--ant-color-text)',
        marginBottom: 40,
        textAlign: 'center',
      }}>
        🌰 推荐你
        <span style={{
          color: 'var(--ant-color-primary)',
          fontWeight: 400,
          fontStyle: 'italic',
          margin: '0 4px 0 8px',
        }}>Cang</span> 一下 🌰
      </h1>

      {/* 2x2 卡片网格 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 280px)',
        gap: 16,
      }}>
        {recommendedItems.map((item) => {
          // 判断类型：有 postCount 的通常是 tagGroup，没有的是 tag
          const itemType: 'tagGroup' | 'tag' = 'postCount' in item ? 'tagGroup' : 'tag';
          // 添加 topImages 并转换 topBloggers 格式
          const itemWithImages = cloudTopImages[item.id]
            ? {
                ...item,
                topImages: cloudTopImages[item.id],
                topBloggers: (cloudTopBloggers[item.id] || []).map(b => ({ avatar: b.avatarUrl || '' })),
              }
            : item;
          return (
            <CloudItemCard
              key={item.id}
              item={itemWithImages}
              onClick={() => onItemClick(item, itemType)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default SquarePage;
